// AutoGrowingTextarea.tsx
import React, { useRef, useEffect, TextareaHTMLAttributes, useCallback, useMemo, CSSProperties } from 'react';

interface AutoGrowingTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'style' | 'onChange' | 'value'> {
  label?: string;
  placeholder?: string;
  description?: string;
  name?: string;
  value?: string | ReadonlyArray<string> | number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  minHeight?: number;
  maxHeight?: number;
  labelClassName?: string;
  descriptionClassName?: string;
  containerClassName?: string;
  textareaClassName?: string;
  textareaStyleOverrides?: CSSProperties;
  onEnterPress?: () => void;
  inlineDecorationRenderer?: (context: {
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
    valueAsString: string;
  }) => React.ReactNode;
}

// Identite stable: un `= {}` en parametre par defaut naitrait a chaque rendu et
// invaliderait le useMemo de mergedTextareaStyle chez tout appelant omettant la prop.
const NO_STYLE_OVERRIDES: CSSProperties = {};

const AutoGrowingTextareaCore: React.FC<AutoGrowingTextareaProps> = ({
  label,
  placeholder,
  description,
  name,
  value = '', // Default to empty string
  onChange,
  minHeight = 60,
  maxHeight = 800,
  labelClassName = "block text-sm font-medium text-white mb-1",
  descriptionClassName = "text-xs text-[#4D4D4D] mt-1",
  containerClassName = "mb-4",
  textareaClassName = "w-full p-2 rounded text-white resize-none placeholder-[#4D4D4D]",
  textareaStyleOverrides = NO_STYLE_OVERRIDES,
  onEnterPress,
  inlineDecorationRenderer,
  onFocus,
  onBlur,
  ...restProps
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSpectacleFlashing, setIsSpectacleFlashing] = React.useState(false);
  const [isTextareaFocused, setIsTextareaFocused] = React.useState(false);
  const prevValueRef = React.useRef(value);
  const lastWidthRef = useRef(0);

  useEffect(() => {
    setIsTextareaFocused(textareaRef.current === document.activeElement);
  }, []);

  React.useEffect(() => {
    const isFocused = textareaRef.current === document.activeElement;
    if (value !== prevValueRef.current && !isFocused) {
      setIsSpectacleFlashing(true);
      const timer = setTimeout(() => setIsSpectacleFlashing(false), 300);
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
    if (value !== prevValueRef.current) {
        prevValueRef.current = value;
    }
  }, [value]);

  const performResize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const originalScrollTop = textarea.scrollTop;
    const currentValue = textarea.value; // Get current value directly from textarea element

    // Temporarily make height auto to measure scrollHeight correctly
    textarea.style.height = 'auto'; 
    // === CRITICAL SECTION START ===
    // The following operations must be synchronous to prevent flicker.
    const currentScrollHeight = textarea.scrollHeight;
    let newHeight = currentScrollHeight;

    // Apply minHeight constraint
    // If the textarea value is empty (after trim) AND minHeight prop is greater than
    // the scrollHeight of an empty line, newHeight should be minHeight.
    // Otherwise, newHeight is the larger of minHeight prop and currentScrollHeight.
    if (currentValue.trim() === "" && minHeight > currentScrollHeight) {
      newHeight = minHeight;
    } else {
      newHeight = Math.max(minHeight, currentScrollHeight);
    }
    
    // Apply maxHeight constraint
    if (newHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'scroll'; // Or 'auto'
      // Restore scroll top ONLY if it was previously scrollable and content is indeed taller
      if (textarea.scrollHeight > maxHeight) { // Check scrollHeight again after setting to maxHeight
          textarea.scrollTop = originalScrollTop;
      }
    } else {
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
    // === CRITICAL SECTION END ===

  }, [minHeight, maxHeight]); // Dependencies are props that affect calculation

  // Effect for resize when 'value' prop changes (handles programmatic changes)
  useEffect(() => {
    // When `value` prop changes, React updates the textarea's DOM value.
    // Then, call performResize directly. This should be synchronous relative to the DOM update.
    performResize(); 
  }, [value, performResize]); // `performResize` changes if minHeight/maxHeight change

  // Effect for initial mount resize (deferred) and for user typing (direct)
  useEffect(() => {
    const ta = textareaRef.current;
    const currentPerformResize = performResize; // Capture for cleanup and listener

    if (ta) {
      // For user typing
      ta.addEventListener('input', currentPerformResize);
      
      // For container resizes. This is the key fix for the issue.
      const observer = new ResizeObserver((entries) => {
        if (!entries || entries.length === 0) return;
        const newWidth = entries[0]?.contentRect.width;
        // Only trigger resize if width has actually changed, to avoid loops.
        // A width of 0 means it's not in the layout, so we shouldn't resize then.
        if ((newWidth !== undefined) && newWidth > 0 && newWidth !== lastWidthRef.current) {
          lastWidthRef.current = newWidth;
          currentPerformResize();
        }
      });
      observer.observe(ta);

      // Initial resize on mount, deferred to allow layout to settle.
      // The observer will also fire on mount, but this is a safe fallback.
      const timeoutId = setTimeout(() => {
        if(textareaRef.current) { // Re-check ref inside timeout
            currentPerformResize();
        }
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
        // Use captured 'ta' which is guaranteed to be non-null here for safer cleanup
        if (ta) {
          ta.removeEventListener('input', currentPerformResize);
        }
      };
    }
  }, [performResize]);

  const memoizedOnChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // `performResize` is already called by the 'input' event listener for user typing.
    // No need to call it here again for that case.
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    restProps.onKeyDown?.(e);

    if (e.isDefaultPrevented()) {
      return;
    }

    if (onEnterPress && e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      onEnterPress();
    }
  }, [onEnterPress, restProps.onKeyDown]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsTextareaFocused(true);
    onFocus?.(event);
  }, [onFocus]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsTextareaFocused(false);
    onBlur?.(event);
  }, [onBlur]);

  const mergedTextareaStyle = useMemo((): CSSProperties => ({
    backgroundColor: '#202020',
    borderRadius: '10px',
    overflowY: 'hidden', // Initial state; performResize will manage it
    minHeight: `${minHeight}px`, // CSS min-height
    // Set initial height to minHeight. performResize (called on mount via useEffect's timeout)
    // will adjust it based on content.
    height: `${minHeight}px`, 
    ...(inlineDecorationRenderer && !isTextareaFocused ? {
      color: 'transparent',
      WebkitTextFillColor: 'transparent',
      caretColor: '#ffffff',
    } : {}),
    ...textareaStyleOverrides,
  }), [inlineDecorationRenderer, isTextareaFocused, minHeight, textareaStyleOverrides]);


  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={name} className={labelClassName}>{label}</label>
      )}
      <div className="relative">
        <textarea
          ref={textareaRef}
          id={name}
          name={name}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={memoizedOnChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`${textareaClassName} ${isSpectacleFlashing ? 'spectacle-flash-textarea' : ''}`}
          style={mergedTextareaStyle}
          rows={1} // Good practice for auto-growing textareas, JS will override height
          {...restProps}
        />
        {inlineDecorationRenderer && inlineDecorationRenderer({
          textareaRef,
          valueAsString: value !== null && value !== undefined ? String(value) : '',
        })}
      </div>
      {description && <p className={descriptionClassName}>{description}</p>}
    </div>
  );
};

const AutoGrowingTextarea = React.memo(AutoGrowingTextareaCore);
export default AutoGrowingTextarea;
